package com.dotdrives.web.pages.settings

import com.dotdrives.controller.ConfigurationController
import com.dotdrives.controller.ConfigurationState
import com.dotdrives.core.uuidV7
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.model.DrivePhase
import com.dotdrives.model.canAdminister
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotButton
import kotlinx.browser.document
import react.*
import react.dom.aria.AriaPressed
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.h3
import react.dom.html.ReactHTML.h4
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.nav
import react.dom.html.ReactHTML.ol
import react.dom.html.ReactHTML.li
import react.dom.html.ReactHTML.form
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.option
import web.dom.ElementId
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.checkbox
import web.html.number
import web.html.submit

external interface ProcessConfigurationPanelProps : Props {
    var controller: ConfigurationController
    var state: ConfigurationState
    var role: String
}

private data class ProcessEditor(
    val requestId: String = uuidV7(),
    val processId: String? = null,
    val phase: DrivePhase? = null,
    val nextOrder: Int = 0,
    val openerId: String,
)

/** Mount-owned editor identity keeps late save acknowledgements out of a later draft. */
val ProcessConfigurationPanel = FC<ProcessConfigurationPanelProps> { props ->
    val state = props.state
    val processes = state.snapshot?.templates.orEmpty()
    val (selectedId, setSelectedId) = useState<String?>(null)
    val selected = processes.firstOrNull { it.id == selectedId } ?: processes.firstOrNull()
    val phases = state.snapshot?.phases.orEmpty().filter { it.templateId == selected?.id }
        .sortedWith(compareBy<DrivePhase> { it.sequence }.thenBy { it.id })
    val (editor, setEditor) = useState<ProcessEditor?>(null)
    val (focusId, setFocusId) = useState<String?>(null)
    val (revealedId, setRevealedId) = useState<String?>(null)
    val canEdit = canAdminister(props.role)

    fun openEditor(value: ProcessEditor) {
        props.controller.clearError()
        setRevealedId(null)
        setEditor(value)
    }
    fun cancelEditor() {
        if (!state.saving) {
            setFocusId(editor?.openerId)
            setEditor(null)
            props.controller.clearError()
        }
    }
    useEffect(state.savedChange) {
        val saved = state.savedChange
        if (saved != null && saved.requestId == editor?.requestId) {
            setSelectedId(saved.processId)
            setRevealedId(saved.itemId)
            setEditor(null)
            setFocusId(if (saved.isPhase) "process-phase-${saved.itemId}" else "process-add-phase")
        }
    }
    useEffect(editor, focusId) {
        if (editor == null && focusId != null) {
            document.getElementById(focusId)?.asDynamic()?.focus()
            setFocusId(null)
        }
    }

    div {
        className = ClassName("process-configuration")
        div {
            key = "heading".unsafeCast<Key>()
            className = ClassName("process-heading")
            div {
                h2 { +"Drive processes" }
                p { +"Shape the steps your team follows with each giver." }
            }
            if (canEdit && editor == null) button {
                id = ElementId("process-new")
                type = ButtonType.buttonType
                className = ClassName("dot-button dot-button-primary")
                disabled = state.snapshot == null
                onClick = { openEditor(ProcessEditor(openerId = "process-new")) }
                +"New process"
            }
        }
        if (state.loading) div { key = "loading".unsafeCast<Key>(); className = ClassName("process-loading"); role = "status".unsafeCast<AriaRole>(); +"Refreshing processes…" }
        state.refreshError?.let { DotBanner { key = "refresh-error".unsafeCast<Key>(); message = it; onRetry = props.controller::load } }
        if (editor == null) state.error?.let { DotBanner { key = "load-error".unsafeCast<Key>(); message = it; onRetry = props.controller::load } }
        val saved = state.savedChange
        if (saved != null && editor == null && saved.processId == selected?.id) div {
            key = "saved".unsafeCast<Key>()
            className = ClassName("process-saved")
            role = "status".unsafeCast<AriaRole>()
            +"${saved.name} saved."
        }
        div {
            // Loading/status changes must not remount the focused workspace after a save.
            key = "workspace".unsafeCast<Key>()
            className = ClassName("process-layout")
            if (processes.isNotEmpty()) div {
                className = ClassName("process-mobile-picker")
                label { htmlFor = ElementId("process-selection"); +"Process" }
                select {
                    id = ElementId("process-selection")
                    value = selected?.id.orEmpty()
                    disabled = editor != null
                    onChange = { setSelectedId(it.currentTarget.value); setRevealedId(null); props.controller.clearError() }
                    processes.forEach { process -> option { value = process.id; +process.name } }
                }
            }
            nav {
                className = ClassName("process-navigation")
                ariaLabel = "Drive processes"
                processes.forEach { process ->
                    val count = state.snapshot?.phases.orEmpty().count { it.templateId == process.id }
                    button {
                        key = process.id.unsafeCast<Key>()
                        type = ButtonType.buttonType
                        className = ClassName("process-navigation-item" + if (selected?.id == process.id) " process-navigation-selected" else "")
                        ariaPressed = (selected?.id == process.id).unsafeCast<AriaPressed>()
                        disabled = editor != null
                        onClick = { setSelectedId(process.id); setRevealedId(null); props.controller.clearError() }
                        span { className = ClassName("process-navigation-name"); +process.name }
                        span { className = ClassName("process-meta"); +"$count ${if (count == 1) "phase" else "phases"}" }
                    }
                }
                if (processes.isEmpty() && state.snapshot != null) p { className = ClassName("process-meta"); +"No processes yet" }
            }
            div {
                className = ClassName("process-workspace")
                if (editor != null) {
                    ProcessEditorForm {
                        key = editor.requestId.unsafeCast<Key>()
                        draft = editor
                        processName = selected?.name.orEmpty()
                        controller = props.controller
                        this.state = state
                        onCancel = ::cancelEditor
                    }
                } else if (selected != null) {
                    div {
                        className = ClassName("process-workspace-heading")
                        div {
                            h3 { +selected.name }
                            p { className = ClassName("process-meta"); +"${phases.size} ${if (phases.size == 1) "phase" else "phases"} in this process" }
                        }
                        if (canEdit && phases.isNotEmpty()) button {
                            id = ElementId("process-add-phase")
                            type = ButtonType.buttonType
                            className = ClassName("dot-button dot-button-secondary")
                            onClick = { openEditor(ProcessEditor(processId = selected.id, nextOrder = (phases.maxOfOrNull { it.sequence } ?: -1) + 1, openerId = "process-add-phase")) }
                            +"Add phase"
                        }
                    }
                    if (phases.isEmpty()) div {
                        className = ClassName("process-empty")
                        h4 { +"Give this process its first step" }
                        p { +if (canEdit) "Add the first phase your team will use when starting a drive." else "An owner or administrator can add phases to this process." }
                        if (canEdit) button {
                            id = ElementId("process-add-phase")
                            type = ButtonType.buttonType
                            className = ClassName("dot-button dot-button-primary")
                            onClick = { openEditor(ProcessEditor(processId = selected.id, openerId = "process-add-phase")) }
                            +"Add first phase"
                        }
                    } else {
                        ol {
                            className = ClassName("process-phase-list")
                            phases.forEach { phase ->
                                li {
                                    key = phase.id.unsafeCast<Key>()
                                    id = ElementId("process-phase-${phase.id}")
                                    tabIndex = -1
                                    className = ClassName("process-phase" + if (revealedId == phase.id) " process-phase-saved" else "")
                                    span { className = ClassName("process-phase-order"); ariaLabel = "Position ${phase.sequence.toLong() + 1}"; +(phase.sequence.toLong() + 1).toString() }
                                    div {
                                        className = ClassName("process-phase-copy")
                                        span { className = ClassName("process-phase-name"); +phase.name }
                                        if (phase.isTerminal) span { className = ClassName("process-closing"); +"Closing phase" }
                                    }
                                    if (canEdit) button {
                                        id = ElementId("process-edit-${phase.id}")
                                        type = ButtonType.buttonType
                                        className = ClassName("process-edit-button")
                                        ariaLabel = "Edit ${phase.name}"
                                        onClick = { openEditor(ProcessEditor(processId = selected.id, phase = phase, openerId = "process-edit-${phase.id}")) }
                                        +"Edit"
                                    }
                                }
                            }
                        }
                        p { className = ClassName("process-footnote"); +"Entering a closing phase asks for the drive’s outcome. Past outcomes stay unchanged." }
                    }
                } else if (state.snapshot != null) div {
                    className = ClassName("process-empty")
                    h3 { +"Start with a process" }
                    p { +if (canEdit) "Name a process, then add the phases your team follows." else "An owner or administrator can create a process for your team." }
                }
            }
        }
    }
}

private external interface ProcessEditorFormProps : Props {
    var draft: ProcessEditor
    var processName: String
    var controller: ConfigurationController
    var state: ConfigurationState
    var onCancel: () -> Unit
}

private val ProcessEditorForm = FC<ProcessEditorFormProps> { props ->
    val draft = props.draft
    val isPhase = draft.processId != null
    val (name, setName) = useState(draft.phase?.name.orEmpty())
    val (order, setOrder) = useState(((draft.phase?.sequence ?: draft.nextOrder).toLong() + 1).toString())
    val (closing, setClosing) = useState(draft.phase?.isTerminal ?: false)
    val saving = props.state.saving
    useEffect(draft.requestId) { document.getElementById("process-editor-name")?.asDynamic()?.focus() }
    useEffect(saving, props.state.error) {
        if (!saving && props.state.error != null) document.getElementById("process-editor-name")?.asDynamic()?.focus()
    }
    form {
        className = ClassName("process-editor")
        ariaLabel = if (isPhase) "Phase editor" else "New process"
        onSubmit = {
            it.preventDefault()
            if (!saving) {
                if (isPhase) props.controller.savePhase(draft.phase, requireNotNull(draft.processId), name, storedPhaseOrder(order), closing, draft.requestId)
                else props.controller.saveTemplate(name, draft.requestId)
            }
        }
        onKeyDown = { if (it.key == "Escape") { it.preventDefault(); if (!saving) props.onCancel() } }
        div {
            className = ClassName("process-editor-body")
            h3 { +if (!isPhase) "New process" else if (draft.phase == null) "Add a phase" else "Edit phase" }
            p { className = ClassName("process-meta"); +if (isPhase) "In ${props.processName}" else "Save a name first. You’ll add phases next." }
            // Keep a stable slot so an error does not replace the input DOM or its focus.
            div { props.state.error?.let { DotBanner { message = it } } }
            label {
                htmlFor = ElementId("process-editor-name")
                +if (isPhase) "Phase name" else "Process name"
            }
            input {
                id = ElementId("process-editor-name")
                value = name
                disabled = saving
                required = true
                onChange = { setName(it.currentTarget.value) }
            }
            if (isPhase) {
                label { htmlFor = ElementId("process-editor-order"); +"Position" }
                input {
                    id = ElementId("process-editor-order")
                    className = ClassName("process-order-input")
                    type = InputType.number
                    min = 1.0
                    step = 1.0
                    required = true
                    disabled = saving
                    value = order
                    onChange = { setOrder(it.currentTarget.value) }
                }
                p { className = ClassName("process-field-help"); +"Positions start at 1. Lower positions appear first; gaps are allowed. Other phases keep their positions." }
                label {
                    className = ClassName("process-closing-field")
                    input { type = InputType.checkbox; checked = closing; disabled = saving; ariaDescribedBy = ElementId("process-closing-help"); onChange = { setClosing(it.currentTarget.checked) } }
                    +"Closing phase"
                }
                p { id = ElementId("process-closing-help"); className = ClassName("process-field-help"); +"Entering this phase asks for an outcome, such as received or pledged. Past outcomes stay unchanged." }
            }
        }
        div {
            className = ClassName("process-editor-footer")
            DotButton { text = "Cancel"; variant = DotButtonVariant.Secondary; enabled = !saving; onClick = props.onCancel }
            button {
                type = ButtonType.submit
                className = ClassName("dot-button dot-button-primary")
                disabled = saving || name.isBlank()
                ariaBusy = saving
                +if (saving) "Saving…" else if (isPhase) "Save phase" else "Save process"
            }
        }
    }
}

/** Translate the visible one-based position only at the write boundary. */
internal fun storedPhaseOrder(position: String): String =
    position.toLongOrNull()?.takeIf { it in 1..(Int.MAX_VALUE.toLong() + 1) }?.minus(1)?.toString() ?: ""
